import { API_CONFIG } from "@/infrastructure/config/api";
import {
  addAuthTokenToUrl,
  redactTokenInUrl,
} from "@/shared/utils/sseConnection";

/**
 * Interface para eventos de execução de agentes
 */
export interface AgentExecutionEvent {
  type: string;
  executionId?: string;
  agentId?: string;
  status?: string;
  timestamp?: string;
  nodeId?: string;
  result?: unknown;
  progress?: number;
  error?: unknown;
  message?: string;
  // Propriedades adicionais que o backend pode enviar
  id?: string;
  agent_base_id?: string;
  agent_version_id?: string;
  started_at?: string;
  created_at?: string;
  updated_at?: string;
  completed_at?: string | null;
  error_message?: string | null;
  metadata?: Record<string, unknown>;
  [key: string]: unknown; // Para propriedades dinâmicas
}

/**
 * Interface para callbacks de eventos de execução de agentes
 */
export interface AgentExecutionCallbacks {
  onExecutionStarted?: (data: AgentExecutionEvent) => void;
  onExecutionProgress?: (data: AgentExecutionEvent) => void;
  onExecutionCompleted?: (data: AgentExecutionEvent) => void;
  onExecutionFailed?: (data: AgentExecutionEvent) => void;
  onExecutionUpdated?: (data: AgentExecutionEvent) => void;
  onNewExecution?: (data: AgentExecutionEvent) => void; // Para new-execution
  onStatusUpdate?: (data: AgentExecutionEvent) => void; // Para status
  onInit?: (data: Record<string, unknown>) => void; // Para init
  onNodeCompleted?: (data: AgentExecutionEvent) => void;
  onNodeStarted?: (data: AgentExecutionEvent) => void;
  onConnect?: (connectionData: Record<string, unknown>) => void;
  onError?: (event: Event) => void;
  onMessage?: (data: Record<string, unknown>) => void; // Para qualquer outro tipo de evento
}

/**
 * Classe para gerenciar conexões SSE para execuções de agentes
 */
export class AgentExecutionsSSE {

  /**
   * Retorna a URL completa para o endpoint de SSE
   * @param path Caminho do endpoint
   * @returns URL completa
   */
  private static getEndpointUrl(path: string): string {
    const baseUrl = API_CONFIG.API_ROOT_URL;
    const fullUrl = `${baseUrl}${path}`;
    return fullUrl;
  }

  /**
   * Cria uma conexão SSE para uma execução específica de agente
   * @param executionId ID da execução a ser monitorada
   * @param callbacks Funções de callback para diferentes tipos de eventos
   * @returns EventSource criado
   */
  static connectToExecution(
    executionId: string,
    callbacks: AgentExecutionCallbacks,
  ): EventSource {

    const baseUrl = this.getEndpointUrl(
      `/agent-base/executions/${executionId}/events`,
    );
    const urlWithTimestamp = `${baseUrl}${baseUrl.includes("?") ? "&" : "?"}_t=${Date.now()}`;

    const sseUrl = addAuthTokenToUrl(urlWithTimestamp);

    // Cria a conexão SSE
    const eventSource = new EventSource(sseUrl);

    // Configura os handlers para diferentes tipos de eventos
    this.setupEventHandlers(eventSource, callbacks);

    return eventSource;
  }

  /**
   * Cria uma conexão SSE para todas as execuções de um agente específico
   * @param agentId ID do agente cujas execuções serão monitoradas
   * @param callbacks Funções de callback para diferentes tipos de eventos
   * @returns EventSource criado
   */
  static connectToAgentExecutions(
    agentId: string,
    callbacks: AgentExecutionCallbacks,
  ): EventSource {

    // Constrói o endpoint correto para SSE de execuções de agentes
    const baseUrl = this.getEndpointUrl(
      `/agent-base/${agentId}/executions/events`,
    );
    const urlWithTimestamp = `${baseUrl}${baseUrl.includes("?") ? "&" : "?"}_t=${Date.now()}`;
    const sseUrl = addAuthTokenToUrl(urlWithTimestamp);

    // Cria a conexão SSE
    const eventSource = new EventSource(sseUrl);

    // Configura os handlers para diferentes tipos de eventos
    this.setupEventHandlers(eventSource, callbacks);

    return eventSource;
  }

  /**
   * Cria uma conexão SSE para todas as execuções de agentes
   * @param callbacks Funções de callback para diferentes tipos de eventos
   * @returns EventSource criado
   */
  static connectToAllExecutions(
    callbacks: AgentExecutionCallbacks,
  ): EventSource {

    const baseUrl = this.getEndpointUrl(`/agent-base/executions/events`);
    const urlWithTimestamp = `${baseUrl}${baseUrl.includes("?") ? "&" : "?"}_t=${Date.now()}`;

    const sseUrl = addAuthTokenToUrl(urlWithTimestamp);

    // Cria a conexão SSE
    const eventSource = new EventSource(sseUrl);

    // Configura os handlers para diferentes tipos de eventos
    this.setupEventHandlers(eventSource, callbacks);

    return eventSource;
  }

  /**
   * Configura os handlers de eventos para o EventSource
   * @param eventSource Instância do EventSource
   * @param callbacks Objeto com callbacks para diferentes tipos de eventos
   */
  private static setupEventHandlers(
    eventSource: EventSource,
    callbacks: AgentExecutionCallbacks,
  ): void {
    // Log de registro de eventos (sem modificar o addEventListener)
    // Handler para evento de conexão estabelecida
    eventSource.addEventListener("connection_established", (event) => {
      try {
        const data = JSON.parse((event as MessageEvent).data);
        callbacks.onConnect?.(data);
      } catch (error) {
        console.error(
          "[AgentExecutionsSSE] Erro ao processar evento de conexão:",
          error,
        );
      }
    });

    // Handler para evento de execução iniciada
    eventSource.addEventListener("execution_started", (event) => {
      try {
        const rawData = (event as MessageEvent).data;

        const data = JSON.parse(rawData);

        if (callbacks.onExecutionStarted) {
          callbacks.onExecutionStarted(data);
        } else {
          console.warn(
            "[AgentExecutionsSSE] Callback onExecutionStarted não definido",
          );
        }
      } catch (error) {
        console.error(
          "[AgentExecutionsSSE] Erro ao processar evento de execução iniciada:",
          error,
        );
      }
    });

    // Handler para evento de progresso de execução
    eventSource.addEventListener("execution_progress", (event) => {
      try {
        const data = JSON.parse((event as MessageEvent).data);
        callbacks.onExecutionProgress?.(data);
      } catch (error) {
        console.error(
          "[AgentExecutionsSSE] Erro ao processar evento de progresso:",
          error,
        );
      }
    });

    // Handler para evento de execução concluída
    eventSource.addEventListener("execution-completed", (event) => {
      try {
        const data = JSON.parse((event as MessageEvent).data);
        callbacks.onExecutionCompleted?.(data);
      } catch (error) {
        console.error(
          "[AgentExecutionsSSE] Erro ao processar evento de conclusão:",
          error,
        );
      }
    });

    // Handler para evento de execução com falha
    eventSource.addEventListener("execution-failed", (event) => {
      try {
        const data = JSON.parse((event as MessageEvent).data);
        callbacks.onExecutionFailed?.(data);
      } catch (error) {
        console.error(
          "[AgentExecutionsSSE] Erro ao processar evento de falha:",
          error,
        );
      }
    });

    // Handler para evento de nó iniciado
    eventSource.addEventListener("node_started", (event) => {
      try {
        const data = JSON.parse((event as MessageEvent).data);
        callbacks.onNodeStarted?.(data);
      } catch (error) {
        console.error(
          "[AgentExecutionsSSE] Erro ao processar evento de nó iniciado:",
          error,
        );
      }
    });

    // Handler para evento de nó concluído
    eventSource.addEventListener("node_completed", (event) => {
      try {
        const data = JSON.parse((event as MessageEvent).data);
        callbacks.onNodeCompleted?.(data);
      } catch (error) {
        console.error(
          "[AgentExecutionsSSE] Erro ao processar evento de nó concluído:",
          error,
        );
      }
    });

    // Handler para evento de conexão estabelecida (connected)
    eventSource.addEventListener("connected", (event) => {
      try {
        const data = JSON.parse((event as MessageEvent).data);
        callbacks.onConnect?.(data);
      } catch (error) {
        console.error(
          "[AgentExecutionsSSE] Erro ao processar evento connected:",
          error,
        );
      }
    });

    // Handler para evento de inicialização (init)
    eventSource.addEventListener("init", (event) => {
      try {
        const data = JSON.parse((event as MessageEvent).data);
        callbacks.onInit?.(data);
      } catch (error) {
        console.error(
          "[AgentExecutionsSSE] Erro ao processar evento init:",
          error,
        );
      }
    });

    // Handler para nova execução (new-execution)
    eventSource.addEventListener("new-execution", (event) => {
      try {
        const data = JSON.parse((event as MessageEvent).data);
        callbacks.onNewExecution?.(data);
      } catch (error) {
        console.error(
          "[AgentExecutionsSSE] Erro ao processar evento new-execution:",
          error,
        );
      }
    });

    // Handler para execução atualizada (execution-updated)
    eventSource.addEventListener("execution-updated", (event) => {
      try {
        const data = JSON.parse((event as MessageEvent).data);
        callbacks.onExecutionUpdated?.(data);
      } catch (error) {
        console.error(
          "[AgentExecutionsSSE] Erro ao processar evento execution-updated:",
          error,
        );
      }
    });

    // Handler para mudança de status (status)
    eventSource.addEventListener("status", (event) => {
      try {
        const data = JSON.parse((event as MessageEvent).data);
        callbacks.onStatusUpdate?.(data);
      } catch (error) {
        console.error(
          "[AgentExecutionsSSE] Erro ao processar evento status:",
          error,
        );
      }
    });

    // Handler genérico para outros eventos não especificados
    eventSource.onmessage = (event) => {
      try {
        // Alguns servidores podem enviar dados sem tipo específico
        const rawData = (event as MessageEvent).data;

        if (!rawData) {
          console.warn("[AgentExecutionsSSE] Evento sem dados recebido");
          return;
        }

        const data = JSON.parse(rawData);

        // Sem inferência de tipo aqui: este handler é o `onmessage`, e o evento
        // que chega nele tem `type === "message"` sempre. O fallback que existia
        // era guardado por `!event.type` e nunca rodou — quem despacha por tipo
        // são os addEventListener acima.

        // Sempre chama o callback genérico se estiver definido
        if (callbacks.onMessage) {
          callbacks.onMessage(data);
        }
      } catch (error) {
        console.error(
          "[AgentExecutionsSSE] Erro ao processar evento genérico:",
          error,
        );
      }
    };

    // Handler para erros de conexão
    eventSource.addEventListener("error", (event) => {
      // Sem o evento cru: `target.url` carrega o access token.
      console.error("[AgentExecutionsSSE] Erro na conexão SSE", {
        readyState: eventSource.readyState,
        url: redactTokenInUrl(eventSource.url),
      });
      // Verifica o estado da conexão
      if (eventSource.readyState === EventSource.CLOSED) {
        console.error(
          "[AgentExecutionsSSE] A conexão SSE foi fechada devido a um erro",
        );
      } else if (eventSource.readyState === EventSource.CONNECTING) {
        console.warn("[AgentExecutionsSSE] Tentando reconectar após erro...");
      }

      if (callbacks.onError) {
        callbacks.onError(event);
      }
      // O navegador tentará reconectar automaticamente
    });
  }
}
